import styled from '@emotion/styled';
import {
  DeleteOutline,
  DownloadOutlined,
  EditOutlined,
  FlashOnOutlined,
  FormatListBulletedOutlined,
  QrCodeOutlined,
} from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import MenuIcon from '@mui/icons-material/Menu';
import {
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import useProductCoddingDelete from 'api/productDetail/useProductCoddingDelete/useProductCoddingDelete';
import DeleteModal from 'components/shared/DeleteModal/DeleteModal';
import { permissions } from 'hooks/permissions/permissions';
import { usePermissions } from 'hooks/permissions/usePermissions';
import React, { useCallback, useMemo, useState } from 'react';

export const CODE_LOGIC = {
  SKU: 'SKU',
  BatchNumber: 'BatchNumber',
  SerialNumber: 'SerialNumber',
};

const coddingPermissions =
  permissions.general.categories.globalSettings.productManagement.productList
    .detail.codding;

const AccordionCodingList = ({
  coddingList,
  getProductDetail,
  refreshProductBranchList,
  setSystemEdit,
  setManualEdit,
  setEditMapping,
  setCreateCoddingGuid,
}) => {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteChosenLocation, setDeleteChosenLocation] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRowInfo, setSelectedRowInfo] = useState(null);

  const { deleteLoading, deleteRequest } = useProductCoddingDelete();

  const { can } = usePermissions();

  const openMenu = Boolean(anchorEl);

  const handleClickMenu = (event, row) => {
    setAnchorEl(event.currentTarget);
    setSelectedRowInfo(row);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedRowInfo(null);
  };

  const deleteItem = useCallback(
    row => () => {
      setDeleteChosenLocation(row);
      setIsDeleteModalOpen(true);
    },
    [],
  );

  const editItem = useCallback(
    row => () => {
      setManualEdit(row);
    },
    [setManualEdit],
  );

  const setDefaultCodeItem = useCallback(
    row => () => {
      // TODO: implement
    },
    [],
  );

  const triggerBatchItem = useCallback(
    row => () => {
      // TODO: implement
    },
    [],
  );

  const showCodeListItem = useCallback(
    row => () => {
      // TODO: implement
    },
    [],
  );

  const downloadCodeListItem = useCallback(
    row => () => {
      // TODO: implement
    },
    [],
  );

  const showSerialListItem = useCallback(
    row => () => {
      // TODO: implement
    },
    [],
  );

  const downloadSerialListItem = useCallback(
    row => () => {
      // TODO: implement
    },
    [],
  );

  const onDeleteSuccess = () => {
    setIsDeleteModalOpen(false);
    refreshProductBranchList();
  };

  const deleteHandle = id => {
    deleteRequest(id, onDeleteSuccess);
  };

  const handleEdit = useCallback(
    packItem => {
      switch (packItem.type) {
        case '0':
          setSystemEdit(packItem.id);
          return;

        case '1':
          editItem(packItem)();
          return;

        case '2':
          setEditMapping(packItem);

          break;
        default:
      }
    },
    [editItem, setEditMapping, setSystemEdit],
  );

  const selectedLogic = selectedRowInfo?.logic;

  const menuItems = useMemo(
    () => [
      {
        label: 'حذف',
        permission: coddingPermissions.delete,
        icon: DeleteOutline,
        color: 'error',

        visible: row => row?.type !== '0',

        onClick: row => {
          handleCloseMenu();
          deleteItem(row)();
        },
      },

      {
        label: 'ویرایش',
        permission: coddingPermissions.edit,
        icon: EditOutlined,
        color: 'warning',

        visible: row => row?.logic === CODE_LOGIC.SKU || !row?.logic,

        onClick: row => {
          handleCloseMenu();
          handleEdit(row);
        },
      },

      {
        label: 'تریگر',
        permission: coddingPermissions.batch.trigger,
        icon: FlashOnOutlined,
        color: 'secondary',

        visible: row => row?.logic === CODE_LOGIC.BatchNumber,

        onClick: row => {
          handleCloseMenu();
          triggerBatchItem(row)();
        },
      },

      {
        label: 'نمایش لیست کد',
        permission: coddingPermissions.batch.view,
        icon: FormatListBulletedOutlined,
        color: 'info',

        visible: row => row?.logic === CODE_LOGIC.BatchNumber,

        onClick: row => {
          handleCloseMenu();
          showCodeListItem(row)();
        },
      },

      {
        label: 'دانلود لیست کد',
        permission: coddingPermissions.batch.download,
        icon: DownloadOutlined,
        color: 'success',

        visible: row => row?.logic === CODE_LOGIC.BatchNumber,

        onClick: row => {
          handleCloseMenu();
          downloadCodeListItem(row)();
        },
      },

      {
        label: 'نمایش لیست سریال ها',
        permission: coddingPermissions.serial.view,
        icon: FormatListBulletedOutlined,
        color: 'info',

        visible: row => row?.logic === CODE_LOGIC.SerialNumber,

        onClick: row => {
          handleCloseMenu();
          showSerialListItem(row)();
        },
      },

      {
        label: 'دانلود لیست سریال ها',
        permission: coddingPermissions.serial.download,
        icon: DownloadOutlined,
        color: 'success',

        visible: row => row?.logic === CODE_LOGIC.SerialNumber,

        onClick: row => {
          handleCloseMenu();
          downloadSerialListItem(row)();
        },
      },

      {
        label: 'کد پیش‌فرض',
        permission: coddingPermissions.setDefault,
        icon: QrCodeOutlined,
        color: 'primary',

        visible: () => true,

        onClick: row => {
          handleCloseMenu();
          setDefaultCodeItem(row)();
        },
      },
    ],
    [
      deleteItem,
      handleEdit,
      setDefaultCodeItem,
      triggerBatchItem,
      showCodeListItem,
      downloadCodeListItem,
      showSerialListItem,
      downloadSerialListItem,
    ],
  );

  const visibleMenuItems = useMemo(() => {
    return menuItems.filter(
      item => item.visible(selectedRowInfo) && can(item.permission),
    );
  }, [menuItems, selectedRowInfo, can]);

  return (
    <Wrapper>
      <Header>
        <Title>لیست کدینگ</Title>

        {can(coddingPermissions.create) && (
          <AddButton
            onClick={setCreateCoddingGuid}
            variant="contained"
            size="small"
          >
            <AddIcon />
          </AddButton>
        )}
      </Header>

      <TableContainer>
        <Table sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow
              sx={{
                '& .MuiTableCell-root': {
                  borderColor: 'rgba(100, 100, 100, .2)',
                  background: 'rgba(180, 180, 180, 0.05)',
                },
              }}
            >
              <TableCell
                align="right"
                sx={{
                  width: {
                    xs: 'auto',
                    lg: '450px',
                    xl: '550px',
                  },
                }}
              >
                نام
              </TableCell>

              <TableCell align="right">کدینگ</TableCell>

              <TableCell align="left" width={100}>
                عملیات ها
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {coddingList?.map((packItem, index) => {
              const rowHasActions = menuItems.some(
                item => item.visible(packItem) && can(item.permission),
              );

              return (
                <TableRow key={packItem.id ?? index}>
                  <TableCell align="right">{packItem.title}</TableCell>

                  <TableCell align="right">{packItem.codding}</TableCell>

                  <TableCell align="right" width={180}>
                    {rowHasActions ? (
                      <ActionContainer>
                        <IconButton
                          id={`${packItem.title}-button`}
                          aria-controls={
                            openMenu ? `${packItem.title}-menu` : undefined
                          }
                          aria-haspopup="true"
                          aria-expanded={openMenu ? 'true' : undefined}
                          onClick={e => handleClickMenu(e, packItem)}
                        >
                          <MenuIcon />
                        </IconButton>
                      </ActionContainer>
                    ) : null}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        <Menu
          anchorEl={anchorEl}
          open={openMenu}
          onClose={handleCloseMenu}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          {visibleMenuItems.map(item => {
            const Icon = item.icon;

            return (
              <MenuItem
                key={item.permission}
                onClick={() => item.onClick(selectedRowInfo)}
              >
                <Action>
                  <Typography fontSize={14}>{item.label}</Typography>

                  <Icon fontSize="small" color={item.color} />
                </Action>
              </MenuItem>
            );
          })}
        </Menu>
      </TableContainer>

      <DeleteModal
        open={isDeleteModalOpen}
        handleClose={() => setIsDeleteModalOpen(false)}
        title={deleteChosenLocation.title}
        locationId={deleteChosenLocation.id}
        deleteLoading={deleteLoading}
        onDelete={deleteHandle}
      />
    </Wrapper>
  );
};

export default AccordionCodingList;

const Wrapper = styled.div`
  margin-top: 50px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 12px;
`;

const Title = styled.h4``;

const AddButton = styled(Button)`
  background-color: #27348b;
`;

const TableContainer = styled.div``;

const ActionContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
`;

const Action = styled(Box)({
  paddingTop: '0.3rem',
  paddingBottom: '0.3rem',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: '0.5rem',
});
